import { ShieldCheckIcon } from 'lucide-react'

import { Button } from '@/components/button/button'

import { SearchCasesSortKey } from '@/lib/queries/business/v4/case/search/types'
import { cn } from '@/lib/utils'

import { CasesTable } from '../ui/cases/cases-table/cases-table'
import { DeleteCasesButton } from '../ui/cases/delete-cases-button/delete-cases-button'
import { TableLoading } from '../ui/table/table-loading/table-loading'
import { CasesFilter } from './ui/cases-filter/cases-filter'
import { CasesPagination } from './ui/pagination/pagination'
import { SelectionStatus } from './ui/selection-status/selection-status'
import { useCasesPage } from './use-page'

export const CasesPage = () => {
  const {
    tableType,
    search,
    pageSize,
    dataCases,
    normalizedTags,
    tagFilterOption,
    normalizedProcessingTimes,
    processingTimeFilterOption,
    normalizedCaseTypes,
    normalizedStatuses,
    normalizedAssignees,
    assigneeFilterOption,
    selection,
    sortKey,
    sortOrder,
    isLoadingCases,
    isFetchingCases,
    isErrorInCasesSearch,
    isEveryRowSelected,
    isLoadingCaseStatusActionButton,
    handleToggleAllRowSelection,
    handleToggleCaseRowSelection,
    handlePaginationChange,
    handleFilterChange,
    handleSortChange,
    handleClickActivateButton,
    handleClickInactivateButton,
  } = useCasesPage()

  return (
    <div className="px-10 pb-32 pt-11">
      <div className={cn('mb-[5px]', 'text-[14px] leading-[20px] text-slate-600')}>Immigration</div>
      <h2 className={cn('text-2xl font-bold', 'mb-8')}>{tableType === 'active' ? 'Active' : 'Inactive'} Cases</h2>

      <div className={cn('flex items-center justify-between', 'mb-4')}>
        <CasesFilter
          disabled={isFetchingCases}
          keyword={search}
          tags={normalizedTags}
          tagFilterOption={tagFilterOption}
          processingTimes={normalizedProcessingTimes}
          processingTimeFilterOption={processingTimeFilterOption}
          caseTypes={normalizedCaseTypes}
          statuses={normalizedStatuses}
          assignees={normalizedAssignees}
          assigneeFilterOption={assigneeFilterOption}
          onChange={handleFilterChange}
        />

        <div className={cn('flex space-x-4')}>
          <Button
            type="button"
            className={cn('flex gap-2')}
            variant="black-secondary"
            size="sm"
            disabled={selection.length === 0}
            loading={isLoadingCaseStatusActionButton}
            onClick={tableType === 'active' ? handleClickInactivateButton : handleClickActivateButton}
          >
            <ShieldCheckIcon size={16} />
            {tableType === 'active' ? 'Inactivate' : 'Activate'}
          </Button>
          <DeleteCasesButton disabled={selection.length === 0} caseIds={selection} />
        </div>
      </div>

      {isLoadingCases ? (
        <TableLoading />
      ) : isErrorInCasesSearch ? (
        <TableLoading />
      ) : (
        <CasesTable
          className={cn(isFetchingCases && 'opacity-50')}
          disabled={isFetchingCases}
          cases={dataCases?.cases ?? []}
          selectedCaseIds={selection}
          isEveryRowSelected={isEveryRowSelected}
          defaultSort={{ key: SearchCasesSortKey.ID, order: 'desc' }}
          currentSort={{ key: sortKey, order: sortOrder }}
          onToggleCaseRowSelection={handleToggleCaseRowSelection}
          onToggleAllRowSelection={handleToggleAllRowSelection}
          onSortChange={handleSortChange}
        />
      )}

      <div className="mt-3 flex items-center justify-between">
        <SelectionStatus selectedCount={selection.length} totalCount={dataCases?.cases?.length ?? 0} />

        <CasesPagination
          disabled={isFetchingCases}
          totalCount={dataCases?.totalCount}
          totalPages={dataCases?.totalPages}
          currentPage={dataCases?.currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
        />
      </div>
    </div>
  )
}
